/**
 * @file Finance CSV builder + downloader.
 *
 * Single source of truth for the wire format Finance imports — matches
 * strv-device-sale's `admin-exports.js` byte-for-byte. Two callers today:
 *   • Pending Abra tab — batch export of every sell/loot row.
 *   • Offboarding workflow — auto-downloads the single row right after the
 *     admin finalises a Mark bought / Send to loot decision, so the CSV
 *     is in hand without a separate trip to Pending Abra.
 *
 * CSV format (do not change without Finance approval):
 *   TYPE OF INVOICE, NAME OF DEVICE, SN, PRICE CZK, EXCL. VAT, INC. VAT, EMAIL
 *
 *   • Every cell wrapped in double quotes.
 *   • Inch-mark `"` in device names is replaced with PRIME `″` — Google
 *     Sheets parses RFC-4180 triple-quote sequences inconsistently.
 *   • Formula-injection guard: cells starting with =/+/-/@/tab/CR get a
 *     leading tab so Excel/Sheets treat them as text.
 *   • CRLF line endings + UTF-8 BOM so Sheets opens the file as a CSV
 *     rather than treating it as opaque text.
 *
 * VAT flag pairs reflect how the admin enters the price for each variant:
 *   • Sell (employee or external buyer) — admin enters the **net** price
 *     (Finance adds VAT on the invoice). EXCL. VAT=TRUE, INC. VAT=FALSE.
 *   • Loot (going to loot.strv.io) — admin enters the **gross** price
 *     (loot.strv.io knows the price is VAT-inclusive, mirrors the
 *     strv-device-sale model). EXCL. VAT=FALSE, INC. VAT=TRUE.
 */

import { el } from '../utils.js';

const HEADERS = ['TYPE OF INVOICE', 'NAME OF DEVICE', 'SN', 'PRICE CZK', 'EXCL. VAT', 'INC. VAT', 'EMAIL'];

/**
 * Build the CSV text. `rows` is a list of objects shaped:
 *   { deviceName, deviceSn, salePriceCzk, buyerEmail }
 * `variant` is 'sell' | 'loot'. Returns the file contents as a string
 * (BOM-prefixed, CRLF-terminated), ready for Blob construction.
 */
export function buildFinanceCsv(rows, variant) {
  const typeOfInvoice = variant === 'loot' ? 'Loot' : 'Device Sale';
  // sell → admin entered net price; loot → admin entered gross. See file
  // header for the rationale.
  const inclVat = variant === 'loot';
  const exclVat = inclVat ? 'FALSE' : 'TRUE';
  const incVat  = inclVat ? 'TRUE'  : 'FALSE';
  const lines = [HEADERS.map(csvQuote).join(',')];
  for (const r of rows) {
    lines.push([
      typeOfInvoice,
      r.deviceName || '',
      r.deviceSn   || '',
      formatPriceCzk(r.salePriceCzk),
      exclVat,
      incVat,
      r.buyerEmail || '',
    ].map(csvQuote).join(','));
  }
  const BOM = '﻿';
  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/** Trigger a browser download of the CSV. Used by both the batch (pending
 *  tab) and single-row (offboarding) callers. Returns the filename used. */
export function downloadFinanceCsv(rows, variant) {
  if (!rows.length) return null;
  const csv = buildFinanceCsv(rows, variant);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const base = variant === 'loot' ? 'gear-loot' : 'gear-sales';
  const filename = `${base}-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.csv`;
  const a = el('a', { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
}

// ── Internals ─────────────────────────────────────────────────────────────

function csvQuote(v) {
  let s = (v == null ? '' : String(v))
    .replace(/"/g, '″')   // inch-mark → PRIME, avoids `"""` triple-quote sequences
    .replace(/"/g, '""'); // RFC-4180 escape (no-op after the prior replace, kept for safety)
  if (s && /^[=+\-@\t\r]/.test(s)) s = `\t${s}`;
  return `"${s}"`;
}

function formatPriceCzk(n) {
  if (n == null || n === '' || !Number.isFinite(Number(n))) return '';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
